/*
  lx-product.js — product-page bundle (plan C6).
  Core: a tiny variant pub/sub replacing gp:product-change-variant.
  Modules register via LxProduct.on('variant', fn) and are fed
  { variant, product } objects. Emitters call LxProduct.setVariant(v).
  Section-specific behaviors (gallery, countdown, strips, stock counter,
  sticky bar) attach themselves when their DOM exists — every module
  null-guards so any section can be absent.

  Loaded by BOTH lx-main-product and lx-product-accordions — the whole file
  is wrapped in a load-once guard so double <script> tags are harmless.
*/
(function () {
  'use strict';
  if (window.__lxProductJsLoaded) return;
  window.__lxProductJsLoaded = true;

  window.LxProduct = (function () {
    const listeners = { variant: [] };
    let current = null;
    return {
      on(event, fn) {
        (listeners[event] = listeners[event] || []).push(fn);
        if (event === 'variant' && current) fn(current);
      },
      setVariant(detail) {
        current = detail;
        (listeners.variant || []).forEach((fn) => {
          try {
            fn(detail);
          } catch (e) {
            console.warn('[lx-product] listener error', e);
          }
        });
        // Doubly Global Currency re-conversion after price rewrites (plan R6)
        if (window.DoublyGlobalCurrency && typeof window.DoublyGlobalCurrency.convertAll === 'function') {
          setTimeout(() => window.DoublyGlobalCurrency.convertAll(), 0);
        }
      },
      get variant() {
        return current;
      },
    };
  })();

  /* ---- shared helpers ---------------------------------------------------- */

  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  let productDataCache;
  function getProductData() {
    if (productDataCache !== undefined) return productDataCache;
    productDataCache = null;
    const el = document.querySelector('[data-lx-product-data]');
    if (el) {
      try {
        productDataCache = JSON.parse(el.textContent);
      } catch (e) {
        console.warn('[lx-product] bad product JSON', e);
      }
    }
    return productDataCache;
  }

  /* Shopify.formatMoney port — money_format emitted ONCE, unquoted via | json
     inside the product JSON (plan R9 fix). Format may embed
     <span class="money"> for Doubly Global Currency. */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }
    let value = '';
    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  /* Countdown module (plan C5): 3h57m persisted timer + delivery-day text.
     localStorage key 'countdown_end_time' MUST match live (plan R20). */
  onReady(function () {
    const el = document.querySelector('[data-lx-countdown]');
    if (!el) return;
    const DURATION = (3 * 60 + 57) * 60 * 1000;
    const KEY = 'countdown_end_time';
    let end = parseInt(localStorage.getItem(KEY) || '0', 10);
    if (!end || end < Date.now()) {
      end = Date.now() + DURATION;
      localStorage.setItem(KEY, String(end));
    }
    const pad = (n) => String(n).padStart(2, '0');
    const tick = () => {
      let ms = end - Date.now();
      if (ms <= 0) {
        end = Date.now() + DURATION;
        localStorage.setItem(KEY, String(end));
        ms = DURATION;
      }
      const h = Math.floor(ms / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      const s = Math.floor((ms % 60000) / 1000);
      el.textContent = `${pad(h)}:${pad(m)}:${pad(s)}`;
    };
    tick();
    setInterval(tick, 1000);

    const day = document.querySelector('[data-lx-delivery-day]');
    if (day) {
      const now = new Date();
      const target = new Date(now);
      if (now.getHours() >= 8) target.setDate(target.getDate() + 1);
      /* i18n (Arabic phase A): strings come from window.lxI18n (emitted by
         lx-main-product.liquid via locale keys); hard-coded English stays as
         the no-Liquid fallback. Month/day VALUES go through Intl so other
         locales localize; the ORDER is owned by the translatable format
         template, keeping English output byte-identical ("Tomorrow, 12 Jul."). */
      const i18n = window.lxI18n || {};
      const label = now.getHours() < 8 ? i18n.today || 'Today' : i18n.tomorrow || 'Tomorrow';
      const months = i18n.months || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      let dd = pad(target.getDate());
      let mon = months[target.getMonth()];
      try {
        const parts = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
          day: '2-digit',
          month: 'short',
        }).formatToParts(target);
        const part = (t) => (parts.find((x) => x.type === t) || {}).value;
        dd = part('day') || dd;
        mon = part('month') || mon;
      } catch (e) {
        /* old hard-coded path remains the fallback */
      }
      const fmt = i18n.deliveryDayFormat || '[label], [dd] [month].';
      day.textContent = fmt.replace('[label]', label).replace('[dd]', dd).replace('[month]', mon);
    }
  });

  /* ---- variant picker (size pills, plan C4) ------------------------------
     NO PRE-SELECTED SIZE (owner batch 2026-07): nothing is chosen on load.
     - emit(variant, isSelection): a real pill click (isSelection=true) writes
       the hidden variant id AND refreshes price/stock/ATC. The load-time DISPLAY
       emit (isSelection=false) only feeds subscribers their starting numbers
       from the first available variant WITHOUT filling the id — so ATC stays
       gated until the shopper picks a size.
     - submit guard (capture, before Dawn's <product-form>): with an empty id we
       block the POST and prompt "select a size" instead of adding a wrong one.
     Mirrors the quickshop's identical recon-item-2 behaviour. */
  onReady(function () {
    const pills = document.querySelector('[data-lx-pills]');
    const data = getProductData();
    if (!data) return;

    const idInput = document.querySelector('[data-lx-variant-id]');
    const form = document.querySelector('.lx-mp__form');
    const errorWrap = document.querySelector('.lx-mp__error');
    const hasOptions = !!pills;

    function findVariant(value) {
      return (data.variants || []).find(
        (v) => v.option1 === value || (Array.isArray(v.options) && v.options[0] === value)
      );
    }

    function emit(variant, isSelection) {
      if (!variant) return;
      if (isSelection && idInput) idInput.value = variant.id;
      window.LxProduct.setVariant({ variant: variant, product: data });
    }

    // "Please select a size" prompt — reuses the product-form error slot and
    // flashes the pills (parity with quickshop.promptSelectSize).
    function promptSelectSize() {
      if (errorWrap) {
        const msg = errorWrap.querySelector('.product-form__error-message') || errorWrap;
        msg.textContent = (window.lxI18n && window.lxI18n.selectSize) || 'Please select a size';
        errorWrap.hidden = false;
      }
      if (pills) {
        pills.classList.remove('is-flash');
        void pills.offsetWidth; // restart the flash
        pills.classList.add('is-flash');
        try {
          pills.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        } catch (e) {
          /* noop */
        }
      }
    }
    function clearSelectSizePrompt() {
      if (errorWrap) errorWrap.hidden = true;
      if (pills) pills.classList.remove('is-flash');
    }

    if (pills) {
      pills.addEventListener('change', function (e) {
        const input = e.target.closest('[data-lx-pill-input]');
        if (!input) return;
        emit(findVariant(input.value), true); // real selection: writes the id
        clearSelectSizePrompt();
        showLowStock(); // keyboard path (arrows fire change without a click)
      });
      // low-stock urgency chip (owner batch 2026-07): EVERY size, EVERY click —
      // including re-clicks of the already-selected pill (change won't re-fire).
      // Click-driven only: on page load nothing is selected, so nothing shows.
      pills.addEventListener('click', function (e) {
        if (!e.target.closest('[data-lx-pill]')) return;
        showLowStock();
      });
    }

    // Guard ATC submit while no size is chosen. Capture phase on the document so
    // it runs BEFORE Dawn's <product-form> form-level (target-phase) listener;
    // stopPropagation there keeps Dawn from POSTing an empty id.
    if (form && hasOptions) {
      document.addEventListener(
        'submit',
        function (e) {
          if (e.target !== form) return;
          if (idInput && idInput.value) return; // a size is chosen — let it POST
          e.preventDefault();
          e.stopPropagation();
          promptSelectSize();
        },
        true
      );
    }

    // 2026-07: the chip sits ABOVE the size header (section block 12c). With the
    // reserved slot removed, the first reveal gently pushes the header down (a
    // one-time shift, not a permanent gap). Trigger contract unchanged:
    // [hidden] off + .is-in re-added per interaction.
    function showLowStock() {
      const chip = document.querySelector('[data-lx-lowstock]');
      if (!chip) return;
      chip.hidden = false;
      chip.classList.remove('is-in');
      void chip.offsetWidth; // restart the 200ms fade/rise per interaction
      chip.classList.add('is-in');
    }

    // Initial DISPLAY emission (no selection): populate price / stock / ATC from
    // the first available variant — matches Liquid's selected_or_first_available
    // — WITHOUT inking a pill or filling the hidden id. Single-variant products
    // have no pills, so their only id IS written here (they need one to buy).
    const displayVariant =
      (data.variants || []).find((v) => v.available) || (data.variants || [])[0];
    if (displayVariant) emit(displayVariant, !hasOptions);
  });

  /* ---- EU/US/UK unit toggle (spec 01 §8.8) -------------------------------
     Rewrites every pill label from its full "EU 40 | US 7 | UK 6" value.
     EU → "40", US → "7 US", UK → "6 UK". EU is clicked on load (trap 7).
     Live also rewrites the hover tooltips — replicated. */
  onReady(function () {
    const btns = Array.from(document.querySelectorAll('[data-lx-unit]'));
    const pills = Array.from(document.querySelectorAll('[data-lx-pill]'));
    if (!btns.length || !pills.length) return;

    const parsed = pills.map(function (pill) {
      const textEl = pill.querySelector('[data-lx-pill-text]');
      const tipEl = pill.querySelector('[data-lx-pill-tooltip-text]');
      const full = (textEl && textEl.getAttribute('data-full-value')) || '';
      const parts = {};
      const regex = /(US|UK|EU)\s+(\d+(?:\.\d+)?)/g;
      let match;
      while ((match = regex.exec(full)) !== null) parts[match[1]] = match[2];
      return { textEl: textEl, tipEl: tipEl, parts: parts };
    });

    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const key = btn.getAttribute('data-lx-unit');
        btns.forEach(function (b) {
          b.classList.remove('lx-unit--active');
        });
        btn.classList.add('lx-unit--active');
        parsed.forEach(function (p) {
          const label = key === 'EU' ? `${p.parts[key] || ''}` : `${p.parts[key] || ''} ${key}`;
          if (p.textEl) p.textEl.textContent = label;
          if (p.tipEl) p.tipEl.textContent = label;
        });
      });
    });

    const btnEu = btns.find(function (b) {
      return b.getAttribute('data-lx-unit') === 'EU';
    });
    if (btnEu) btnEu.click();
  });

  /* ---- price row + 4-payments (spec 01 §8.10) ---------------------------- */
  onReady(function () {
    const data = getProductData();
    if (!data || !data.moneyFormat) return;
    const priceEl = document.querySelector('[data-lx-price]');
    const compareEl = document.querySelector('[data-lx-compare]');
    const quarterEls = document.querySelectorAll('[data-lx-quarter]');
    if (!priceEl && !compareEl && !quarterEls.length) return;

    window.LxProduct.on('variant', function (detail) {
      const v = detail.variant;
      if (!v) return;
      if (priceEl) priceEl.innerHTML = formatMoney(v.price, data.moneyFormat);
      if (compareEl) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          compareEl.innerHTML = formatMoney(v.compare_at_price, data.moneyFormat);
          compareEl.removeAttribute('data-hidden');
        } else {
          compareEl.setAttribute('data-hidden', 'true');
        }
      }
      quarterEls.forEach(function (el) {
        /* floor, not round: live truncates the 4-pay quarter at cent
           precision (657.5¢ renders 6.57) — matches Liquid divided_by: 4 */
        el.innerHTML = formatMoney(Math.floor(v.price / 4), data.moneyFormat);
      });
    });
  });

  /* ---- media gallery (spec 01 §8.1/8.2) ---------------------------------- */
  onReady(function () {
    const gallery = document.querySelector('[data-lx-gallery]');
    if (!gallery) return;
    const viewport = gallery.querySelector('[data-lx-gal-viewport]');
    const track = gallery.querySelector('[data-lx-gal-track]');
    const slides = Array.from(gallery.querySelectorAll('[data-lx-gal-slide]'));
    const thumbs = Array.from(gallery.querySelectorAll('[data-lx-gal-thumb]'));
    if (!viewport || !track || !slides.length) return;

    let index = parseInt(track.getAttribute('data-start-index') || '0', 10) || 0;
    // RTL-aware paging: in Arabic the flex track lays slides right-to-left with slide
    // 0 flush to the viewport's right edge, so slide i is reached with a POSITIVE
    // translateX (LTR uses negative). Swipe direction mirrors too (see endDrag).
    const rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    function basePct(i) { return (rtl ? i : -i) * 100; }

    function goTo(next, skipAnimation) {
      index = Math.max(0, Math.min(slides.length - 1, next));
      track.style.transition = skipAnimation ? 'none' : '';
      track.style.transform = `translateX(${basePct(index)}%)`;
      if (skipAnimation) void track.offsetWidth; // flush, restore transition
      if (skipAnimation) track.style.transition = '';
      thumbs.forEach(function (t) {
        t.setAttribute('data-active', String(parseInt(t.getAttribute('data-index'), 10) === index));
      });
      // pause any playing media when leaving its slide
      slides.forEach(function (slide, i) {
        if (i !== index) {
          slide.querySelectorAll('video').forEach(function (v) {
            v.pause();
          });
        }
      });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(parseInt(thumb.getAttribute('data-index'), 10) || 0);
      });
    });

    // lazy video: click play → load data-src, play, hide overlay
    gallery.querySelectorAll('[data-lx-gal-video]').forEach(function (wrap) {
      const video = wrap.querySelector('video');
      const play = wrap.querySelector('[data-lx-gal-play]');
      if (!video || !play) return;
      play.addEventListener('click', function () {
        if (!video.src && video.dataset.src) video.src = video.dataset.src;
        play.style.display = 'none';
        video.play();
      });
      video.addEventListener('play', function () {
        play.style.display = 'none';
      });
    });

    // variant featured media swap
    window.LxProduct.on('variant', function (detail) {
      const v = detail.variant;
      const mediaId = v && (v.featured_media_id || (v.featured_media && v.featured_media.id));
      if (!mediaId) return;
      const target = slides.find(function (s) {
        return s.getAttribute('data-media-id') === String(mediaId);
      });
      if (target) goTo(parseInt(target.getAttribute('data-index'), 10) || 0);
    });

    // drag / swipe
    let startX = null;
    let dragging = false;
    viewport.addEventListener('pointerdown', function (e) {
      if (e.target.closest('video, iframe, button')) return;
      startX = e.clientX;
      dragging = true;
      track.style.transition = 'none';
      viewport.setPointerCapture(e.pointerId);
    });
    viewport.addEventListener('pointermove', function (e) {
      if (!dragging || startX === null) return;
      const dx = e.clientX - startX;
      track.style.transform = `translateX(calc(${basePct(index)}% + ${dx}px))`;
    });
    function endDrag(e) {
      if (!dragging || startX === null) return;
      dragging = false;
      track.style.transition = '';
      const dx = e.clientX - startX;
      startX = null;
      const threshold = Math.min(50, viewport.clientWidth * 0.15);
      // + = advance (next), direction-agnostic: LTR swipes left, RTL swipes right.
      const logical = rtl ? dx : -dx;
      if (logical > threshold) goTo(index + 1);
      else if (logical < -threshold) goTo(index - 1);
      else goTo(index);
    }
    viewport.addEventListener('pointerup', endDrag);
    viewport.addEventListener('pointercancel', endDrag);

    goTo(index, true);
  });

  /* ---- dark-strip text rotator (spec 01 §8.3: in 3000ms, out at 2800ms) --- */
  onReady(function () {
    const rotator = document.querySelector('[data-lx-rotator]');
    if (!rotator) return;
    const titles = rotator.querySelectorAll('.text-change');
    if (!titles.length) return;
    let index = 0;
    const IN_TIMER = 3000;
    const OUT_TIMER = 2800;
    function animateText() {
      for (let i = 0; i < titles.length; i++) titles[i].classList.remove('text-in', 'text-out');
      titles[index].classList.add('text-in');
      setTimeout(function () {
        titles[index].classList.add('text-out');
      }, OUT_TIMER);
      setTimeout(function () {
        index = index === titles.length - 1 ? 0 : index + 1;
        animateText();
      }, IN_TIMER);
    }
    animateText();
  });

  /* ---- show-more description (spec 01 §8.17, 147-char preview) ------------ */
  onReady(function () {
    const box = document.querySelector('[data-lx-desc]');
    if (!box) return;
    const dataEl = box.querySelector('[data-lx-desc-data]');
    const textEl = box.querySelector('[data-lx-desc-text]');
    const toggleEl = box.querySelector('[data-lx-desc-toggle]');
    if (!dataEl || !textEl || !toggleEl) return;
    let payload = null;
    try {
      payload = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    const strongText = (payload && payload.strong) || '';
    const descHtml = (payload && payload.desc) || '';
    const strongHtml = strongText ? `<strong>${strongText}</strong>` : '';
    const LIMIT = 147;

    function htmlToText(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html || '';
      return (tmp.textContent || tmp.innerText || '').trim();
    }
    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text || '';
      return div.innerHTML;
    }

    const descText = htmlToText(descHtml);
    const hasBold = !!strongText;
    const spaceIfNeeded = hasBold && descText.length ? ' ' : '';
    const fullHtml = (hasBold ? strongHtml + spaceIfNeeded : '') + (descHtml || '');
    const combinedText = ((hasBold ? strongText + ' ' : '') + descText).trim();
    let previewHtml = fullHtml;
    let isExpanded = false;

    if (!combinedText.length) {
      textEl.textContent = '';
      toggleEl.style.display = 'none';
      return;
    }
    if (combinedText.length > LIMIT) {
      const budget = Math.max(0, LIMIT - (hasBold ? strongText.length + 1 : 0));
      previewHtml = (hasBold ? strongHtml + spaceIfNeeded : '') + escapeHtml(descText.slice(0, budget)) + '...';
      textEl.innerHTML = previewHtml;
      toggleEl.textContent = (window.lxI18n && window.lxI18n.showMore) || 'Show More';
      toggleEl.style.display = 'inline';
    } else {
      textEl.innerHTML = fullHtml;
      toggleEl.style.display = 'none';
    }

    toggleEl.addEventListener('click', function () {
      if (isExpanded) {
        textEl.innerHTML = previewHtml;
        toggleEl.textContent = (window.lxI18n && window.lxI18n.showMore) || 'Show More';
      } else {
        textEl.innerHTML = fullHtml;
        toggleEl.textContent = (window.lxI18n && window.lxI18n.showLess) || 'Show Less';
      }
      isExpanded = !isExpanded;
    });
  });

  /* ---- accordions (C2 consumer; single-expand, 500ms grid-rows) ----------- */
  onReady(function () {
    document.querySelectorAll('[data-lx-accordion]').forEach(function (group) {
      const items = Array.from(group.querySelectorAll('[data-lx-acc-item]'));
      items.forEach(function (item) {
        const header = item.querySelector('[data-lx-acc-header]');
        if (!header) return;
        header.addEventListener('click', function () {
          const isOpen = item.getAttribute('data-open') === 'true';
          items.forEach(function (other) {
            other.setAttribute('data-open', 'false');
            const h = other.querySelector('[data-lx-acc-header]');
            if (h) h.setAttribute('aria-expanded', 'false');
          });
          if (!isOpen) {
            item.setAttribute('data-open', 'true');
            header.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  });

  /* ---- dialog triggers (size guide S9, pay-later S10) ---------------------
     Elements with data-lx-dialog="<id>" open <dialog id>. No-ops (and blocks
     the hash jump) while the target section is not built yet. */
  onReady(function () {
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-lx-dialog]');
      if (!trigger) return;
      const id = trigger.getAttribute('data-lx-dialog');
      if (!id) return;
      const dialog = document.getElementById(id);
      if (trigger.tagName === 'A' || (dialog && typeof dialog.showModal === 'function')) e.preventDefault();
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
    });
  });

  /* ---- add to cart: availability + success flow (plan R19) ---------------
     Submission itself is Dawn's <product-form> custom element; we listen on
     Dawn's pub/sub for the post-add hooks. */
  onReady(function () {
    const atc = document.querySelector('[data-lx-atc]');
    const data = getProductData();

    if (atc && data) {
      const textEl = atc.querySelector('[data-lx-atc-text]');
      const labels = data.labels || {};
      window.LxProduct.on('variant', function (detail) {
        const v = detail.variant;
        if (!v) return;
        if (v.available) {
          atc.removeAttribute('disabled');
          atc.removeAttribute('aria-disabled');
          atc.classList.remove('lx-atc--soldout');
          if (textEl) textEl.textContent = labels.addToCart || 'Add to cart';
        } else {
          atc.setAttribute('disabled', 'disabled');
          atc.setAttribute('aria-disabled', 'true');
          atc.classList.add('lx-atc--soldout');
          if (textEl) textEl.textContent = labels.outOfStock || 'Out of stock';
        }
      });
    }

    // success message + Thank You flash contract.
    // NOTE: PUB_SUB_EVENTS is a top-level `const` in Dawn's constants.js —
    // it is NOT on window; bare-identifier typeof guards are required.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (!event || event.source !== 'product-form') return;
        if (data && Array.isArray(data.variants)) {
          const belongs = data.variants.some(function (v) {
            return String(v.id) === String(event.productVariantId);
          });
          if (!belongs) return;
        }
        const slot = document.querySelector('[data-lx-atc-success]');
        if (slot) {
          const icon =
            '<svg height="20" width="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor"><path fill="currentColor" d="M243.31,90.91l-128.4,128.4a16,16,0,0,1-22.62,0l-71.62-72a16,16,0,0,1,0-22.61l20-20a16,16,0,0,1,22.58,0L104,144.22l96.76-95.57a16,16,0,0,1,22.59,0l19.95,19.54A16,16,0,0,1,243.31,90.91Z"></path></svg>';
          const lxRoot = (window.lxI18n && window.lxI18n.root) || '';
          const addedMsg =
            (window.lxI18n && window.lxI18n.addedToCartHtml) ||
            'Added to cart! <a href="' +
              lxRoot +
              '/cart">View Cart</a> or <a href="' +
              lxRoot +
              '/collections/all">Continue Shopping</a>';
          slot.innerHTML = `${icon} ${addedMsg}`;
        }
        // 3s "Thank You!" flash on any opted-in button (sticky bar, plan R19)
        document.querySelectorAll('[data-lx-thankyou]').forEach(function (el) {
          el.classList.add('lx-show-thankyou');
          setTimeout(function () {
            el.classList.remove('lx-show-thankyou');
          }, 3000);
        });
        document.dispatchEvent(
          new CustomEvent('lx:atc-success', {
            detail: { productVariantId: event.productVariantId, cartData: event.cartData },
          })
        );
      });
    }
  });

  /* ---- WhatsApp button: Zoko widget trigger (plan R4, null-guarded) ------- */
  onReady(function () {
    const btn = document.querySelector('[data-lx-whatsapp]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      const zokoWhatsapp = document.getElementById('zoko_chat-widget-channel-wrapper-whatsapp');
      if (zokoWhatsapp) {
        zokoWhatsapp.click();
        return;
      }
      const zokoWrapper = document.getElementById('zoko-8aec0844-b70f-11ec-b909-0242ac120002');
      if (zokoWrapper) {
        const floatingImg = zokoWrapper.querySelector('img');
        if (floatingImg) floatingImg.click();
      }
      setTimeout(function () {
        const channel = document.getElementById('zoko_chat-widget-channel-wrapper-whatsapp');
        if (channel) channel.click();
      }, 150);
    });
  });

  /* ---- trust tile 2: wa.me from the current URL (matches live) ------------ */
  onReady(function () {
    const tile = document.querySelector('.lx-trust__tile--chat');
    if (!tile) return;
    const msgTpl =
      (window.lxI18n && window.lxI18n.whatsappPrefill) ||
      "Hello L'EXUDE! I am interested in this product: [url]";
    const msg = msgTpl.replace('[url]', window.location.href);
    tile.href = `https://wa.me/971505051909?text=${encodeURIComponent(msg)}`;
  });

  /* ---- stock counter (spec 01 §8.15) -------------------------------------- */
  onReady(function () {
    const stock = document.querySelector('[data-lx-stock]');
    const data = getProductData();
    if (!stock || !data) return;
    const msgEl = stock.querySelector('[data-lx-stock-msg]');
    const barEl = stock.querySelector('[data-lx-stock-bar]');
    const messages = data.stockMessages || {};
    const inventory = data.inventory || {};

    window.LxProduct.on('variant', function (detail) {
      const v = detail.variant;
      if (!v || !msgEl) return;
      const inv = inventory[String(v.id)] || {};
      const qty = typeof inv.qty === 'number' ? inv.qty : 0;
      const management = inv.management || null;
      const policy = inv.policy || 'deny';
      let html;
      if (!management && qty <= 0) html = messages.unlimited;
      else if (!v.available) html = messages.outOfStock;
      else if (v.available && policy === 'continue' && qty <= 0) html = messages.continueSelling;
      else html = (messages.template || '').replace(/<\$quantity\$>/g, qty);
      if (html != null) msgEl.innerHTML = html;
      if (barEl) barEl.hidden = !v.available;
    });
  });
})();
